import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
} from '@material-ui/core'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { makeStyles } from '@material-ui/core/styles'
import PropTypes from 'prop-types'
import { useMutation, useQueryClient } from 'react-query'
import createStage from '../../../../Apis/stages/createStage'
import updateStage from '../../../../Apis/stages/updateStage'
import QueryKeys from '../../../../utils/constants/queryKeys'
import editStageModalStyles from './editStageModal.styles'
import CalendarIcon from '../../../../assets/icons/calendarIcon'

const useStyles = makeStyles(editStageModalStyles)

const EditStageModal = (props) => {
  const { isOpen, onClose, stageData, orgId, seasonId } = props
  const isEditMode = Boolean(stageData)
  const classes = useStyles()
  const queryClient = useQueryClient()

  const [formData, setFormData] = useState({
    name: '',
    startDate: null,
    endDate: null,
  })

  useEffect(() => {
    if (stageData) {
      setFormData({
        name: stageData.name || '',
        startDate: stageData.startDate ? new Date(stageData.startDate) : null,
        endDate: stageData.endDate ? new Date(stageData.endDate) : null,
      })
    } else {
      setFormData({ name: '', startDate: null, endDate: null })
    }
  }, [stageData, isOpen])

  const createStageMutation = useMutation(
    (data) => createStage({ orgId, seasonId, data }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.GET_STAGES, orgId, seasonId])
        onClose()
      },
    }
  )

  const updateStageMutation = useMutation(
    ({ stageId, data }) => updateStage({ orgId, seasonId, stageId, data }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.GET_STAGES, orgId, seasonId])
        onClose()
      },
    }
  )

  const handleInputChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value })
  }

  const handleDateChange = (field) => (date) => {
    setFormData({ ...formData, [field]: date })
  }

  const handleSubmit = () => {
    const payload = {
      seasonId,
      name: formData.name,
      startDate: formData.startDate ? formData.startDate.toISOString() : null,
      endDate: formData.endDate ? formData.endDate.toISOString() : null,
    }

    if (isEditMode) {
      updateStageMutation.mutate({
        stageId: stageData.id,
        data: payload,
      })
    } else {
      createStageMutation.mutate(payload)
    }
  }

  const isLoading = createStageMutation.isLoading || updateStageMutation.isLoading

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        {isEditMode ? 'Edit Stage' : 'Create New Stage'}
      </DialogTitle>
      <DialogContent>
        <TextField
          label='Stage Name'
          fullWidth
          value={formData.name}
          onChange={handleInputChange('name')}
          className={classes.dialogField}
          margin='normal'
        />
        <KeyboardDatePicker
          label='Start Date'
          format='dd/MM/yyyy'
          fullWidth
          value={formData.startDate}
          onChange={handleDateChange('startDate')}
          className={classes.dialogField}
          margin='normal'
          inputVariant='outlined'
          keyboardIcon={<CalendarIcon />}
          InputLabelProps={{ shrink: true }}
        />
        <KeyboardDatePicker
          label='End Date'
          format='dd/MM/yyyy'
          fullWidth
          value={formData.endDate}
          onChange={handleDateChange('endDate')}
          className={classes.dialogField}
          margin='normal'
          inputVariant='outlined'
          keyboardIcon={<CalendarIcon />}
          InputLabelProps={{ shrink: true }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          color='primary'
          variant='contained'
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress size={24} />
          ) : isEditMode ? (
            'Update'
          ) : (
            'Create'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

EditStageModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  stageData: PropTypes.object,
  orgId: PropTypes.string.isRequired,
  seasonId: PropTypes.string.isRequired,
}

export default EditStageModal
